import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  DestroyRef,
  OnDestroy,
} from '@angular/core';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { environment } from '@pt/environment';
import {
  MentoringPreview,
  MentoringTaskData,
} from '@pt/models';
import { CommonMediaQueries, SliderControlsModule } from '@hglobal/common';
import { SwiperModule } from 'swiper/angular';
import { MentoringStageComponent, MentoringTaskDetailsDialog } from '@pt/components';
import { SwiperOptions, Mousewheel } from 'swiper';

import {
  MentoringPreviewApiMockService,
  MENTORING_PREVIEW_API,
  MentoringPreviewService,
} from './api';

@Component({
  selector: 'pu-mentoring-preview',
  templateUrl: './mentoring-preview.component.html',
  styleUrls: ['./mentoring-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SwiperModule, SliderControlsModule, MentoringStageComponent],
  providers: [
    {
      provide: MENTORING_PREVIEW_API,
      useClass: environment.useMocks ? MentoringPreviewApiMockService : MentoringPreviewService,
    },
  ],
})
export class MentoringPreviewComponent implements OnDestroy {
  readonly #router = inject(Router);
  readonly #destroyRef = inject(DestroyRef);
  readonly #mentoringPreviewService = inject(MENTORING_PREVIEW_API);
  readonly #mentoringTaskDetailsDialog = inject(MentoringTaskDetailsDialog);

  readonly mentoringDetails = signal<MentoringPreview | null>(null);

  readonly sliderConfig: SwiperOptions = {
    slidesPerView: 1,
    spaceBetween: 16,
    grabCursor: true,
    modules: [Mousewheel],
    mousewheel: { forceToAxis: true },
    navigation: {
      prevEl: '.pt-onboarding-preview__swiper-control-prev',
      nextEl: '.pt-onboarding-preview__swiper-control-next',
    },
    breakpoints: {
      [CommonMediaQueries.MD]: { slidesPerView: 2, spaceBetween: 16 },
      [CommonMediaQueries.LG]: { slidesPerView: 'auto', spaceBetween: 32 },
    },
  };

  constructor() {
    this.#getMentoringDetails();
  }

  openTask({ task, userMentoringId }: MentoringTaskData) {
    task.completed = false;

    this.#mentoringTaskDetailsDialog.open({
      task,
      mode: 'preview',
      userMentoringId,
    });
  }

  ngOnDestroy(): void {
    localStorage.removeItem('mentoringDetails');
  }

  #getMentoringDetails(): void {
    const cached = localStorage.getItem('mentoringDetails');
    if (cached) {
      this.mentoringDetails.set(JSON.parse(cached));
      return;
    }

    const state = this.#router.getCurrentNavigation()?.extras.state ?? history.state;
    const id = state?.['id'];

    if (id) {
      this.#mentoringPreviewService
        .getMentoringDetails(id)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(details => {
          this.mentoringDetails.set(details);
          localStorage.setItem('mentoringDetails', JSON.stringify(details));
        });
    } else {
      this.mentoringDetails.set(state);
    }
  }
}
